import { useMemo } from "react";
import {
  Download,
  Eye,
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  type LucideIcon,
} from "lucide-react";
import type { LookbookSharedFile } from "@/api/raise-board-api";
import { Button } from "@/components/ui/button";
import { FilePreviewDialog, getFileType } from "@/components/common/file-preview-dialog";
import { fileTypeLabel } from "@/lib/utils";
import { DealSection } from "./lookbook-section-nav";
import { lookbookDocumentUrl } from "./lookbook-visibility";

interface LookbookDataRoomProps {
  id?: string;
  files: LookbookSharedFile[];
  publicId: string;
  companyId: string;
}

/**
 * The documents a lookbook shares for one deal.
 *
 * A deliberately small read-only view rather than the pipeline's data room:
 * that component is two thousand lines of upload, delete, versioning, Google
 * Drive and DocSend, all of it needing an authenticated workspace. None of it
 * means anything to someone holding a link.
 *
 * Urls here point at storage, not at the gated proxy: the proxy serves no bytes
 * in production, so both buttons were dead for every file. Interim, and it does
 * cost something — a storage url someone has copied keeps working after the
 * document is unticked, which the proxy is what made revocable. Only documents
 * on the entry's allowlist ever carry one.
 *
 * Viewing is the shared FilePreviewDialog, the same dialog the pipeline data
 * room opens. Opening the storage url in a new tab was the previous answer and
 * could never have worked: uploads store objects either with an explicit
 * `Content-Disposition: attachment` (DocSend, Drive, inbound email) or with no
 * content type at all (manual upload), so the tab downloads the file and
 * closes instead of rendering it. The dialog sidesteps that — the viewers it
 * embeds fetch the bytes rather than navigating to them.
 */
export function LookbookDataRoom({
  id,
  files,
  publicId,
  companyId,
}: LookbookDataRoomProps) {
  const groups = useMemo(() => {
    const byType = new Map<string, LookbookSharedFile[]>();
    for (const file of files) {
      const existing = byType.get(file.file_type);
      if (existing) existing.push(file);
      else byType.set(file.file_type, [file]);
    }
    return [...byType.entries()]
      .map(([fileType, items]) => ({
        fileType,
        label: fileTypeLabel(fileType),
        items,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [files]);

  return (
    <DealSection
      id={id}
      title="Data room"
      count={files.length}
      meta={`${files.length} document${files.length === 1 ? "" : "s"} shared`}
    >
      {files.length === 0 ? (
        <p className="py-8 text-[15px] text-muted-foreground">
          No documents have been shared for this company.
        </p>
      ) : (
        <div>
          {groups.map((group) => (
            <div
              key={group.fileType}
              className="grid gap-x-8 border-b border-border py-5 sm:grid-cols-[11rem_minmax(0,1fr)]"
            >
              <h3 className="pb-2 pt-2.5 text-[12px] font-medium uppercase tracking-[0.14em] text-muted-foreground sm:pb-0">
                {group.label}
              </h3>
              <ul className="divide-y divide-border/80">
                {group.items.map((file) => {
                  const Icon = iconForFile(file.file_name);
                  const url = lookbookDocumentUrl(file, publicId, companyId);
                  const previewType = previewFileType(file, url);
                  const meta = [file.file_size, formatDate(file.time_stamp)]
                    .filter(Boolean)
                    .join(", ");
                  return (
                    <li
                      key={file._id}
                      className="-mx-3 flex items-center gap-3.5 rounded-lg px-3 py-2.5 transition-colors duration-150 hover:bg-card"
                    >
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[15px] font-medium">
                          {file.file_name}
                        </p>
                        {meta && (
                          <p className="text-xs text-muted-foreground">{meta}</p>
                        )}
                      </div>
                      {previewType && (
                        <FilePreviewDialog
                          url={url}
                          fileName={file.file_name}
                          /* Only read for spreadsheets, where the viewer has
                             to fetch the bytes and a storage url sends no
                             CORS header. /view_file is unauthenticated, so it
                             works for a lookbook viewer as it does in-app. */
                          fileId={file._id}
                          typeOverride={previewType}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                            aria-label={`Preview ${file.file_name}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </FilePreviewDialog>
                      )}
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                      >
                        {/* `download` is honoured on the proxy fallback and
                            ignored on a cross-origin storage url, where the
                            browser saves what it cannot render and opens the
                            rest. Still worth setting: it is what the fallback
                            needs, and it names the saved file. */}
                        <a
                          href={lookbookDocumentUrl(file, publicId, companyId, {
                            download: true,
                          })}
                          download={file.file_name}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`Download ${file.file_name}`}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </DealSection>
  );
}

const EXTENSION_ICONS: Array<[RegExp, LucideIcon]> = [
  [/\.(pdf)$/i, FileText],
  [/\.(csv|xlsx?|numbers)$/i, FileSpreadsheet],
  [/\.(png|jpe?g|gif|webp|svg)$/i, FileImage],
  [/\.(docx?|txt|md|rtf)$/i, FileText],
];

function iconForFile(fileName: string): LucideIcon {
  for (const [pattern, icon] of EXTENSION_ICONS) {
    if (pattern.test(fileName)) return icon;
  }
  return FileIcon;
}

/**
 * Extensions FilePreviewDialog has a real branch for. Anything outside this
 * set falls through to its generic iframe, which for a storage url means the
 * browser downloads the file instead of showing it — an eye icon that
 * silently downloads is worse than no eye icon, so those stay download-only.
 *
 * Mirrors KNOWN_PREVIEW_EXTENSIONS in the pipeline data room.
 */
const PREVIEWABLE_EXTENSIONS = new Set([
  "pdf",
  "ppt",
  "pptx",
  "doc",
  "docx",
  "xlsx",
  "xls",
  "csv",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "ico",
]);

/**
 * The extension to preview a shared document as, or null when it has none we
 * can render.
 *
 * Prefers the stored filename and falls back to the url, the same order the
 * pipeline data room uses: portal uploads persist `file_name` without an
 * extension ("pitch_deck"), and a signed storage url's query string parses as
 * a bogus one ("pdf?X-Goog-Signature=..."). Either alone misses files the
 * other resolves.
 *
 * Note this hands the url to docs.google.com / view.officeapps.live.com for
 * PDF, Word and PowerPoint, which fetch it server-side — the same third
 * parties the in-app preview already uses, now also for lookbook viewers.
 * Spreadsheets and images are rendered locally.
 */
function previewFileType(
  file: LookbookSharedFile,
  url: string
): string | null {
  const fromName = getFileType(file.file_name);
  if (PREVIEWABLE_EXTENSIONS.has(fromName)) return fromName;
  const fromUrl = getFileType(url);
  return PREVIEWABLE_EXTENSIONS.has(fromUrl) ? fromUrl : null;
}

function formatDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
